import { WSAlert } from "@wingspanhq/fe-component-library";
import { PayerPayeeLinkRequest } from "@wingspanhq/wingspan-api-sdk";
import React from "react";
import { queriesLinkRequests } from "@query";
import { useSession } from "@hooks/useSession";

import { ErrorAlert } from "@components/ErrorAlert";

type Props = {
  /**
   * Only the id is read — the accepting account comes from the session. Typed to
   * just that so the alert takes either the owner-side `PayerPayeeLinkRequest`
   * or the narrower row `/link-requests/incoming` returns.
   */
  linkRequest: Pick<PayerPayeeLinkRequest, "id">;
};

/**
 * A single pending v3 invitation the logged-in user can accept. Accepting links
 * the two accounts.
 *
 * `acceptingAccountId` is **required** in the request body — the deployed API
 * rejects the call without it (422 `ValidationError` on `acceptingAccountId`).
 * It is the session's account: that is the account the person is acting as, and
 * the only one reachable here. Resolving a "principal" account instead would
 * mean `listPersonStakeholders`, which is **501 Not Implemented** on v3
 * ("scaffolded but not implemented yet"), so it can only fail.
 *
 * The payer/payee lists are invalidated alongside the link-request list from the
 * mutation's registry config.
 */
export const PendingInvitationAlert: React.FC<Props> = props => {
  const { accountId } = useSession();

  const [acceptLinkRequest, acceptMeta] =
    queriesLinkRequests.acceptLinkRequest.useMutation();

  const handleAccept = () => {
    if (!accountId) {
      return;
    }

    acceptLinkRequest({
      linkRequestId: props.linkRequest.id,
      xWingspanAccount: accountId,
      linkRequestAccept: { acceptingAccountId: accountId }
    });
  };

  return (
    <>
      <WSAlert
        theme="info"
        icon="envelope"
        title="Pending invitation"
        action={{
          text: "Accept invitation",
          onClick: handleAccept,
          loading: acceptMeta.isLoading
        }}
      >
        You have a pending invitation to connect on Wingspan. Accept to start
        working together.
      </WSAlert>

      <ErrorAlert context="Other" error={acceptMeta.error} mt="M" />
    </>
  );
};
